// =============================================================
// CookiePolicy — "Legal document" layout
// -------------------------------------------------------------
// Reuses the shared shell primitives in components/legal/DocShell
// so this page stays in lockstep with Terms & Conditions.
//
// Cookie types render as a clean ledger, browser steps as cards.
// The Portuguese copy is unchanged; the duplicate per-browser
// "note" boilerplate is consolidated into a single help-note.
// =============================================================

import React from 'react';

import Header from 'components/Header';
import Footer from 'components/Footer';

import { DocHero, Toc, DocShell, DocSection, IntroNote, P } from 'components/legal/DocShell';

import {
  CheckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ShareIcon,
  MegaphoneIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// ------------------------------------------------------------------
// DATA — Portuguese copy, unchanged (shortened display title + Icon)
// ------------------------------------------------------------------
const cookieTypes = [
  {
    title: 'Essenciais',
    Icon: CheckIcon,
    description:
      'Alguns cookies são essenciais para aceder a áreas específicas dos nossos sites. Permitem a navegação no site e a utilização das suas aplicações, tal como aceder a áreas seguras do site através de login. Sem estes cookies, os serviços respetivos não podem ser prestados.',
  },
  {
    title: 'Analíticos',
    Icon: ChartBarIcon,
    description:
      'Utilizamos estes cookies para analisar a forma como os utilizadores usam o site e monitorizar a performance deste. Isto permite-nos fornecer uma experiência de alta qualidade ao personalizar a nossa oferta e rapidamente identificar e corrigir quaisquer problemas que surjam. Por exemplo, usamos cookies de desempenho para saber quais as páginas mais populares, qual o método de ligação entre páginas que é mais eficaz, ou para determinar a razão de algumas páginas estarem a receber mensagens de erro. Baseado na utilização do site, podemos também utilizar estes cookies para destacar artigos ou serviços do site que pensamos ser do interesse dos utilizadores. Estes cookies são utilizados apenas para efeitos de criação e análise estatística, sem nunca recolher informação de caráter pessoal.',
  },
  {
    title: 'De funcionalidade',
    Icon: Cog6ToothIcon,
    description:
      'Utilizamos cookies de funcionalidade para nos permitir relembrar as preferências do utilizador. Por exemplo, os cookies evitam digitar o nome do utilizador cada vez que este acede ao site. Também usamos cookies de funcionalidade para fornecer serviços avançados ao utilizador, como por exemplo efetuar comentários a um artigo. Em resumo, os cookies de funcionalidade guardam as preferências do utilizador relativamente à utilização do site, de forma que não seja necessário voltar a configurar o site cada vez que o visita.',
  },
  {
    title: 'De terceiros',
    Icon: ShareIcon,
    description:
      'Servem para medir o sucesso de aplicações e a eficácia da publicidade de terceiros.',
  },
  {
    title: 'De publicidade',
    Icon: MegaphoneIcon,
    description:
      'Servem para direcionar a publicidade em função dos interesses de cada utilizador, permitindo limitar o número de vezes do visionamento do anúncio. Estes cookies ajudam a medir a eficácia da publicidade. No entanto, não identificam o utilizador.',
  },
];

const browserInstructions = [
  {
    browser: 'Google Chrome',
    steps: [
      'Clique no menu do Chrome (canto superior direito da barra de ferramentas do navegador).',
      'Selecione "Definições".',
      'Clique em "Mostrar definições avançadas".',
      'Na secção "Privacidade", clique no botão "Definições de conteúdo".',
      'Escolha as definições de cookies desejadas.',
    ],
  },
  {
    browser: 'Microsoft Internet Explorer',
    steps: [
      'Clique no menu "Ferramentas" e escolha a opção "Opções de Internet".',
      'Selecione o separador "Privacidade".',
      'Mova a barra de acordo com o nível de privacidade pretendido (no topo surgirá "Bloquear todos os cookies").',
    ],
  },
  {
    browser: 'Mozilla Firefox',
    steps: [
      'Clique no menu "Ferramentas".',
      'Selecione "Opções".',
      'Clique no ícone "Privacidade", que encontra no painel superior.',
      'Na secção "Cookies" escolha a sua opção.',
      'Clique em "OK" para guardar as alterações e fechar.',
    ],
  },
  {
    browser: 'Safari',
    steps: [
      'Na barra de Menu, clique em "Editar".',
      'Selecione "Preferências".',
      'No painel superior, selecione o ícone "Privacidade".',
      'Na secção "Aceitar cookies", selecione a sua opção.',
    ],
  },
];

// ------------------------------------------------------------------
// page-specific blocks
// ------------------------------------------------------------------
const CookieRow = ({ Icon, title, description }) => (
  <div className="grid grid-cols-1 gap-2 border-b border-line py-[22px] first:border-t first:border-line sm:grid-cols-[200px_1fr] sm:gap-6">
    <div className="flex items-start gap-3">
      <span className="grid h-[26px] w-[26px] flex-none place-items-center rounded-lg bg-brand-soft text-yellow-04">
        <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      </span>
      <h3 className="mt-[3px] font-display text-[15.5px] font-bold leading-[1.25] text-ink">
        {title}
      </h3>
    </div>
    <p className="m-0 text-[15px] leading-[1.65] text-ink-3">{description}</p>
  </div>
);

const DurationCard = ({ kicker, title, children }) => (
  <div className="rounded-2xl border border-line bg-paper-2 px-[22px] pb-5 pt-[22px]">
    <div className="mb-2.5 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-yellow-04">
      {kicker}
    </div>
    <h3 className="mb-2.5 font-sans text-[19px] font-extrabold tracking-[-0.01em] text-ink-strong">{title}</h3>
    <p className="m-0 text-[14.5px] leading-[1.6] text-ink-3">{children}</p>
  </div>
);

const BrowserCard = ({ browser, steps }) => (
  <div className="rounded-2xl border border-line bg-white px-[22px] pb-5 pt-[22px]">
    <div className="mb-4 flex items-center gap-2.5">
      <span className="grid h-[30px] w-[30px] place-items-center rounded-lg border border-line bg-paper-2 font-display text-[12px] font-bold text-ink-2">
        {browser.charAt(0)}
      </span>
      <h3 className="m-0 font-display text-[15px] font-bold text-ink">{browser}</h3>
    </div>
    <ol className="m-0 list-none p-0 [counter-reset:bstep]">
      {steps.map((step, i) => (
        <li
          key={i}
          className="relative mb-[9px] pl-[26px] text-[13.5px] leading-[1.5] text-ink-3 [counter-increment:bstep] before:absolute before:left-0 before:top-0 before:grid before:h-[18px] before:w-[18px] before:place-items-center before:rounded-full before:border before:border-line-strong before:bg-paper-2 before:font-display before:text-[10px] before:font-bold before:text-muted before:[content:counter(bstep)]"
        >
          {step}
        </li>
      ))}
    </ol>
  </div>
);

const tocItems = [
  { id: 'c1', label: 'O que são cookies?' },
  { id: 'c2', label: 'Para que servem os cookies' },
  { id: 'c3', label: 'Que tipo de cookies utilizamos' },
  { id: 'c4', label: 'Duração dos cookies' },
  { id: 'c5', label: 'Como bloquear ou desativar' },
  { id: 'c6', label: 'Medição de audiência sem cookies' },
];

const CookiePolicyPage = () => (
  <div className="min-h-screen flex flex-col bg-paper">
    <Header />

    <main className="flex-1">
      <DocHero title="Política de Cookies" />

      <DocShell toc={<Toc items={tocItems} />}>
        <IntroNote>
          Usamos cookies para fazer a There2b funcionar e para melhorar a sua experiência. Esta
          política explica o que são, para que servem e como pode controlá-los a qualquer momento.
        </IntroNote>

        <DocSection id="c1" index={1} title="O que são cookies?">
          <P>
            Os cookies são pequenos ficheiros de texto que um site, ao ser visitado pelo utilizador,
            coloca no seu computador ou no seu dispositivo móvel através do navegador de internet
            (browser). A colocação de cookies ajudará o site a reconhecer o seu dispositivo na(s)
            próxima(s) vez(es) que o utilizador o visita. Usamos o termo cookies nesta política para
            referir todos os ficheiros que recolhem informações desta forma e o termo site para
            referir a plataforma online disponível no uniform resource locator (URL) www.there2b.com.
          </P>
          <P>
            Os cookies utilizados não recolhem informação que identifica o utilizador. Os cookies
            recolhem informações genéricas, designadamente a forma como os utilizadores chegam e
            utilizam os sites ou a zona do país/países através do qual acedem ao site, etc. Os
            cookies retêm apenas informação relacionada com as suas preferências.
          </P>
          <P>
            A qualquer momento o utilizador pode, através do seu navegador de internet (browser)
            decidir ser notificado sobre a receção de cookies, bem como bloquear a respetiva entrada
            no seu sistema.
          </P>
          <P>
            A recusa de uso de cookies no site, pode resultar na impossibilidade de ter acesso a
            algumas das suas áreas ou de receber informação personalizada.
          </P>
        </DocSection>

        <DocSection id="c2" index={2} title="Para que servem os cookies">
          <P>
            Os cookies são usados para ajudar a determinar a utilidade, interesse e o número de
            utilizações dos sites, permitindo uma navegação mais rápida e eficiente e eliminando a
            necessidade de introduzir repetidamente as mesmas informações.
          </P>
        </DocSection>

        <DocSection id="c3" index={3} title="Que tipo de cookies utilizamos">
          <P>Os nossos cookies têm diferentes funções:</P>
          <div className="my-2">
            {cookieTypes.map((ck) => (
              <CookieRow key={ck.title} Icon={ck.Icon} title={ck.title} description={ck.description} />
            ))}
          </div>
        </DocSection>

        <DocSection id="c4" index={4} title="Duração dos cookies">
          <P>Quanto ao tempo em que permanecem no seu dispositivo, os cookies utilizados podem ser:</P>
          <div className="my-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DurationCard kicker="Persistência" title="Permanentes">
              Ficam armazenados ao nível do navegador de internet (browser) nos seus dispositivos de
              acesso (computador, telemóvel, tablet, etc.) e são utilizados sempre que o utilizador
              faz uma nova visita ao site. Geralmente são utilizados para direcionar a navegação de
              acordo com os interesses do utilizador, permitindo-nos prestar um serviço mais
              personalizado.
            </DurationCard>
            <DurationCard kicker="Temporários" title="De sessão">
              São temporários, permanecem nos cookies do seu navegador de internet (browser) até sair
              do site. A informação obtida permite identificar problemas e fornecer uma melhor
              experiência de navegação.
            </DurationCard>
          </div>
        </DocSection>

        <DocSection id="c5" index={5} title="Como bloquear ou desativar">
          <P>
            Como explicámos, os cookies ajudam-no a tirar o máximo proveito dos nossos sites, razão
            porque ao desativar os cookies, partes do nosso site podem não funcionar corretamente.
          </P>
          <P>
            Sem prejuízo, o utilizador pode sempre desativar parte ou a totalidade dos nossos
            cookies, da seguinte forma:
          </P>

          <div className="my-3 mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {browserInstructions.map((b) => (
              <BrowserCard key={b.browser} browser={b.browser} steps={b.steps} />
            ))}
          </div>

          <div className="mt-3.5 flex items-start gap-3 rounded-xl border border-yellow-04/15 bg-brand-soft px-[18px] py-4">
            <InformationCircleIcon className="mt-px h-[18px] w-[18px] flex-none text-yellow-04" />
            <p className="m-0 text-[14.5px] leading-[1.55] text-ink-2">
              Para outros navegadores, procure no menu "Ajuda" ou contacte o fornecedor do navegador.
              Para mais informação sobre cookies, consulte{' '}
              <a
                href="http://www.allaboutcookies.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-yellow-04"
              >
                allaboutcookies.org
              </a>
              .
            </p>
          </div>
        </DocSection>

        <DocSection id="c6" index={6} title="Medição de audiência sem cookies">
          <P>
            Para saber quantas pessoas visitam a There2b e que páginas consultam, usamos uma medição
            própria que não coloca cookies nem guarda qualquer informação no seu dispositivo, e que
            não recorre a serviços de terceiros como o Google Analytics.
          </P>
          <P>
            O seu endereço IP não é guardado. Contamos visitantes através de um código
            pseudonimizado que muda todos os dias, pelo que não é possível reconhecê-lo de um dia
            para o outro. Os dados servem apenas para estatísticas internas e não são partilhados
            com terceiros. Mais detalhes na secção "Dados pessoais" dos{' '}
            <a href="/privacy#t4" className="font-semibold text-yellow-04">
              Termos e Condições
            </a>
            .
          </P>
        </DocSection>
      </DocShell>
    </main>

    <Footer />
  </div>
);

export default CookiePolicyPage;
